/** The two facts the admin header states, fetched once for all five screens.
 *
 * The header carries an open-issue count on the Issues tab and "job last ran
 * 02:00 UTC · 12 s · ok" on the right, and it is the same header on Costs as
 * on the Dashboard. Both figures come from `GET /api/admin/dashboard`, so
 * without a shared cache every hop between admin tabs would re-fetch the
 * whole dashboard — five cards' worth of counting — to redraw one line of
 * eleven-pixel type.
 *
 * Hence a module-level cache with a short life and a single in-flight
 * request: two components mounting together share one fetch, a tab change
 * inside the window redraws from memory, and a screen that has just loaded
 * the dashboard for its own sake seeds the cache instead of provoking a
 * second read. `invalidate` exists because the header's own numbers go stale
 * the moment an admin acts — running the job or resolving an issue changes
 * exactly these two figures.
 *
 * A failure here is deliberately quiet. The header line is context, not
 * content: every screen reports its own errors in its own body, and an
 * alarming banner where a timestamp should be would say the page is broken
 * when only the timestamp is missing.
 */

import { useEffect, useState } from 'react';
import { adminDashboard } from '@/lib/api';
import type { AdminDashboard, JobRun } from '@/lib/types';

export interface AdminPulse {
  /** Issues waiting for a decision, all severities. */
  openIssues: number;
  /** The most recent run, finished or still going. */
  lastRun: JobRun | null;
}

/** How long a reading stays good. Long enough to cover a walk across the
 *  tabs, short enough that a page left open catches the nightly run. */
const TTL_MS = 30_000;

let cached: { value: AdminPulse; at: number } | null = null;
let inFlight: Promise<AdminPulse> | null = null;

/** A count the Issues screen counted itself. Held apart from the cached
 *  reading rather than written into it: a screen that knows one of the two
 *  facts must not leave the other looking like an answered question, or every
 *  header for the next half minute would report a deployment that has never
 *  run its merge job. */
let issueOverride: number | null = null;

/** Every mounted header, so a seed or a refetch reaches all of them. */
const listeners = new Set<(value: AdminPulse) => void>();

/** The two facts as they stand, or null while neither is known. */
function current(): AdminPulse | null {
  if (!cached && issueOverride === null) return null;
  return {
    openIssues: issueOverride ?? cached?.value.openIssues ?? 0,
    lastRun: cached?.value.lastRun ?? null,
  };
}

function emit(): void {
  const value = current();
  if (!value) return;
  for (const listener of listeners) listener(value);
}

function fromDashboard(dashboard: AdminDashboard): AdminPulse {
  return { openIssues: dashboard.issues.open.total, lastRun: dashboard.last_job_run };
}

/** The Dashboard already holds this; it hands it over rather than making the
 *  header ask for the same document a second time. Its count is the whole
 *  queue's, so it supersedes whatever a filtered screen last reported. */
export function seedAdminPulse(dashboard: AdminDashboard): void {
  cached = { value: fromDashboard(dashboard), at: Date.now() };
  issueOverride = null;
  emit();
}

/** The Issues screen counts open issues more often than the dashboard does,
 *  and after every resolution. */
export function setAdminPulseIssues(openIssues: number): void {
  issueOverride = openIssues;
  emit();
}

/** After running the job or resolving an issue: the next reader re-asks. */
export function invalidateAdminPulse(): void {
  cached = null;
  inFlight = null;
  issueOverride = null;
}

async function load(): Promise<AdminPulse> {
  const fresh = cached && Date.now() - cached.at < TTL_MS ? current() : null;
  if (fresh) return fresh;

  if (!inFlight) {
    inFlight = adminDashboard()
      .then((dashboard) => {
        seedAdminPulse(dashboard);
        return fromDashboard(dashboard);
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return await inFlight;
}

export interface AdminPulseState {
  pulse: AdminPulse | null;
  loading: boolean;
}

/**
 * The cached reading, refreshed when it has gone stale.
 *
 * `enabled` is false for a header whose screen passed both figures in as
 * props — the hook still runs, because hooks must, but it asks for nothing.
 */
export function useAdminPulse(enabled = true): AdminPulseState {
  const [pulse, setPulse] = useState<AdminPulse | null>(() => current());
  const [loading, setLoading] = useState(enabled && cached === null);

  useEffect(() => {
    if (!enabled) return undefined;

    let alive = true;
    listeners.add(setPulse);
    setPulse(current());
    if (cached === null) setLoading(true);

    void load()
      .catch(() => null)
      .then(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
      listeners.delete(setPulse);
    };
  }, [enabled]);

  return { pulse, loading };
}
